import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { updatePreferredLanguage } from "services/userService";
import { normalizeUserUiLanguage } from "constants/uiLocales";
import { getAccessToken } from "services/tokenStorage";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const getPreferredLanguage = () => {
    try {
      const token = getAccessToken();
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      // When logged in, prefer profile language over stale `selectedLanguage`
      if (token && user?.language) {
        return normalizeUserUiLanguage(user.language);
      }
    } catch (error) {
      // ignore
    }

    const savedLanguage = localStorage.getItem("selectedLanguage");
    if (savedLanguage) return savedLanguage;

    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return normalizeUserUiLanguage(user?.language) || "en";
    } catch (error) {
      return "en";
    }
  };

  const [language, setLanguage] = useState(() => {
    return getPreferredLanguage();
  });
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // False until the first translation load settles (success or failure)
  const [isReady, setIsReady] = useState(false);

  /* Switches language only once its translations are loaded, setting both in
     the same render. Changing `language` first and loading afterwards showed
     the screen in the old language until the file arrived — e.g. the dashboard
     header flashing English before Marathi right after login. */
  const applyLanguage = useCallback(async (lang) => {
    setIsLoading(true);
    let data = {};
    try {
      const translationModule = await import(`../locales/${lang}/common.json`);
      data = translationModule.default || translationModule;
    } catch (error) {
      console.error(`Failed to load ${lang} translations:`, error);
      // Fallback to English
      try {
        const fallbackModule = await import("../locales/en/common.json");
        data = fallbackModule.default || fallbackModule;
      } catch (fallbackError) {
        console.error("Failed to load fallback translations:", fallbackError);
      }
    }
    setTranslations(data);
    setLanguage(lang);
    localStorage.setItem("selectedLanguage", lang);
    setIsLoading(false);
    setIsReady(true);
  }, []);

  // First load, for the language picked from the profile / localStorage. Runs
  // once: later switches go through setLanguage below.
  useEffect(() => {
    applyLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Resolves once the new language is on screen, so callers that await it (the
     sign-in page before navigating) never show the old one. */
  const setLanguageStable = useCallback(
    async (lang, options = {}) => {
      const persist = options.persist !== false;
      if (lang !== language) {
        await applyLanguage(lang);
        if (persist) {
          const token = getAccessToken();
          if (token) {
            try {
              await updatePreferredLanguage(lang);
              const user = JSON.parse(localStorage.getItem("user") || "{}");
              localStorage.setItem("user", JSON.stringify({ ...user, language: lang }));
            } catch (error) {
              console.error("Failed to persist language preference:", error);
            }
          }
        }
      }
    },
    [language, applyLanguage]
  );

  const value = {
    language,
    setLanguage: setLanguageStable,
    translations,
    isLoading,
    isReady,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
